import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { OptimizedImage } from '../optimized-image/optimized-image';
import clsx from 'clsx';

import { NFT_IMAGES, TierKey } from 'modules/nfts/constants';
import { tierIdToTierKey } from 'modules/nfts/utils/utils';

import { MovesEnum } from 'shared/constants/shared-enums';
import { useQuestsStore } from 'shared/store/questsStore';

import mark from 'assets/png/mark.png?format=webp;avif&as=picture';

import { MOVE_BUTTON_STYLES } from './move-icon.constants';
import styles from './move-icon.module.scss';
import { IMoveIconProps, Status } from './move-icon.types';

const HOLD_DURATION_MS = 1000;

const TEXT_TO_MOVE: Record<'Rock' | 'Paper' | 'Scissors', MovesEnum> = {
  Rock: MovesEnum.Rock,
  Paper: MovesEnum.Paper,
  Scissors: MovesEnum.Scissors,
};

export const MoveIcon = ({
  clickHandler,
  text,
  imgSrc,
  imgSrcNotActive,
  status,
  tabIndex = 0,
  disabled = false,
}: IMoveIconProps) => {
  const { t } = useTranslation();

  const { bestTierPerMove } = useQuestsStore();

  const [progress, setProgress] = useState(0);

  const isHoldingRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const startHold = useCallback(() => {
    if (disabled) return;

    isHoldingRef.current = true;
    setProgress(0);
    startTimeRef.current = Date.now();

    const step = () => {
      if (!startTimeRef.current) return;

      const elapsed = Date.now() - startTimeRef.current;
      const pct = Math.min((elapsed / HOLD_DURATION_MS) * 100, 100);
      setProgress(pct);

      if (pct < 100 && isHoldingRef.current) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else if (pct >= 100 && isHoldingRef.current) {
        isHoldingRef.current = false;
        clickHandler?.();
        setProgress(0);
      }
    };

    animationFrameRef.current = requestAnimationFrame(step);
  }, [disabled, clickHandler]);

  const cancelHold = useCallback(() => {
    isHoldingRef.current = false;
    setProgress(0);

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const colors = useMemo(() => MOVE_BUTTON_STYLES[status], [status]);

  const {
    bestTierImage, // if user owns a higher-tier NFT for this move
  } = useMemo(() => {
    // if someone passes unexpected text, gracefully fall back to generic path
    const moveEnum = TEXT_TO_MOVE[text as keyof typeof TEXT_TO_MOVE];
    if (!moveEnum)
      return { moveEnum, bestTierKey: null as TierKey | null, bestTierImage: null as any };

    const tierId = bestTierPerMove?.[moveEnum] ?? null;
    const bestTierKey = tierIdToTierKey(tierId); // your helper already handles null -> 'none' (if so)

    // only use special artwork if user actually has a tier for this move
    const bestTierImage = tierId != null ? (NFT_IMAGES[bestTierKey]?.[moveEnum] ?? null) : null;

    return { moveEnum, bestTierKey, bestTierImage };
  }, [bestTierPerMove, text]);

  const isSelected = status === Status.Selected;

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      clickHandler?.();
    }
  };

  // ——— Variant A: user has a special NFT image for this move
  if (bestTierImage) {
    return (
      <div
        onMouseDown={startHold}
        onMouseUp={cancelHold}
        onMouseLeave={cancelHold}
        onTouchStart={startHold}
        onTouchEnd={cancelHold}
        onKeyDown={onKeyDown}
        tabIndex={disabled ? -1 : tabIndex}
        role="button"
        aria-label={`${t(text)} - ${status}`}
        aria-pressed={isSelected}
        aria-disabled={disabled}
        className={clsx(
          styles['move-border'],
          'group relative flex w-[3.875rem] shrink-0 flex-col items-center justify-end gap-4 overflow-hidden rounded-[0.5rem] p-[0.125rem] transition-all duration-300',
          'sm:w-[6rem] sm:gap-[1.375rem] sm:rounded-[0.875rem]',
          'xlg:w-[7.5rem] xlg:gap-[1.875rem] xlg:rounded-[1.125rem]',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        )}
        style={
          {
            '--fill': `${progress}`,
          } as React.CSSProperties
        }
      >
        <OptimizedImage
          imageData={bestTierImage}
          alt={t(text)}
          className={clsx(
            'transition-all duration-100',
            isHoldingRef.current && progress < 100 && 'brightness-75',
          )}
        />
      </div>
    );
  }

  // ——— Variant B: generic button with provided img + label
  return (
    <div
      onMouseDown={startHold}
      onMouseUp={cancelHold}
      onMouseLeave={cancelHold}
      onTouchStart={startHold}
      onTouchEnd={cancelHold}
      onKeyDown={onKeyDown}
      tabIndex={disabled ? -1 : tabIndex}
      role="button"
      aria-label={`${t(text)} - ${status}`}
      aria-pressed={isSelected}
      aria-disabled={disabled}
      className={clsx(
        styles['move-border'],
        'group relative flex h-[5.25rem] w-[3.875rem] shrink-0 flex-col items-center justify-end gap-4 overflow-hidden rounded-[0.5rem] p-[0.125rem] transition-all duration-300',
        'sm:h-[8rem] sm:w-[6rem] sm:gap-[1.375rem] sm:rounded-[0.75rem]',
        'xlg:h-[10rem] xlg:w-[7.25rem] xlg:gap-[1.875rem]',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
      )}
      style={
        {
          '--fill': `${progress}`,
          background: colors.border,
        } as React.CSSProperties
      }
    >
      <div
        className={clsx(
          'relative flex h-full w-full flex-col items-center justify-end gap-4 overflow-hidden rounded-[0.375rem] transition-all duration-300 sm:gap-[1.375rem] sm:rounded-[0.75rem] xlg:gap-[1.875rem] xlg:rounded-[1rem]',
          isHoldingRef.current && progress < 100 && 'brightness-75',
        )}
        style={{ background: colors.background }}
      >
        {isSelected && (
          <OptimizedImage
            imageData={mark}
            className="absolute right-0 top-0 w-[25%]"
            alt="selected"
          />
        )}

        <div className="relative h-[1.875rem] w-[2.25rem] shrink-0 sm:h-[3rem] sm:w-[3.5rem] xlg:h-[3.75rem] xlg:w-[4.375rem]">
          <OptimizedImage
            imageData={status === Status.Disabled ? (imgSrcNotActive ?? imgSrc) : imgSrc}
            className="absolute inset-0 h-full w-full transition-all duration-300 group-hover:scale-110"
            style={{ objectFit: 'cover' }}
            alt={t(text)}
          />
        </div>

        <div
          className="relative flex h-[1.125rem] w-full shrink-0 items-center justify-center overflow-hidden rounded-bl-[0.375rem] rounded-br-[0.375rem] border-t-2 pb-3 pl-6 pr-6 pt-3 transition-all duration-300 sm:h-9 sm:rounded-bl-[0.75rem] sm:rounded-br-[0.75rem] xlg:rounded-bl-[1rem] xlg:rounded-br-[1rem]"
          style={{
            borderTopColor: colors.bottomBorder,
            background: colors.bottomBackground,
          }}
        >
          <span className="text-center text-[0.625rem] font-semibold leading-[0.625rem] text-white transition-all duration-300 sm:text-xl sm:leading-5">
            {t(text)}
          </span>
        </div>
      </div>
    </div>
  );
};
